import { normalizeScore, normalizeAltmanScore, normalizeFulmerScore, normalizeSpringateScore, normalizeTafflerScore } from './models';

/**
 * Adjust model result with qualitative factors
 * @param {Object} result - Original model result
 * @param {Object} qualitativeFactors - Qualitative factors object
 * @param {string} model - Model name
 * @returns {Object} Adjusted result
 */
export const adjustResultWithQualitativeFactors = (result, qualitativeFactors, model) => {
  const adjustedResult = { ...result };
  const adjustments = {
    creditHistory: { excellent: 0.1, good: 0.05, average: 0, poor: -0.1, very_poor: -0.2 },
    pendingLawsuits: -0.15,
    managementChanges: -0.1,
    delayedPayments: -0.15,
    supplierRelationships: { excellent: 0.1, good: 0.05, average: 0, problematic: -0.1, critical: -0.2 }
  };

  let scoreAdjustment = 0;
  if (qualitativeFactors.creditHistory) scoreAdjustment += adjustments.creditHistory[qualitativeFactors.creditHistory] || 0;
  if (qualitativeFactors.pendingLawsuits) scoreAdjustment += adjustments.pendingLawsuits;
  if (qualitativeFactors.managementChanges) scoreAdjustment += adjustments.managementChanges;
  if (qualitativeFactors.delayedPayments) scoreAdjustment += adjustments.delayedPayments;
  if (qualitativeFactors.supplierRelationships) scoreAdjustment += adjustments.supplierRelationships[qualitativeFactors.supplierRelationships] || 0;

  let adjustedScore;
  switch (model) {
    case 'altman-z':
    case 'fulmer':
    case 'springate':
    case 'taffler':
    case 'beaver':
    case 'saifullin-kadykov':
      adjustedScore = result.score + scoreAdjustment;
      break;
    default:
      adjustedScore = result.score;
  }

  adjustedResult.score = adjustedScore;
  const thresholds = result.thresholds;

  if (model === 'altman-z') {
    if (adjustedScore > thresholds.safe) {
      adjustedResult.riskLevel = 'Низкий';
      adjustedResult.interpretation = 'Финансово устойчивое состояние.';
    } else if (adjustedScore > thresholds.gray) {
      adjustedResult.riskLevel = 'Умеренный';
      adjustedResult.interpretation = 'Неопределенное положение.';
    } else if (adjustedScore > thresholds.distress * 0.7) {
      adjustedResult.riskLevel = 'Высокий';
      adjustedResult.interpretation = 'Высокая вероятность банкротства.';
    } else {
      adjustedResult.riskLevel = 'Очень высокий';
      adjustedResult.interpretation = 'Критическое положение.';
    }
  } else if (model === 'fulmer') {
    if (adjustedScore > 0) {
      adjustedResult.riskLevel = 'Низкий';
      adjustedResult.interpretation = 'Компания устойчива.';
    } else if (adjustedScore > -0.5) {
      adjustedResult.riskLevel = 'Умеренный';
      adjustedResult.interpretation = 'Неопределенное положение.';
    } else if (adjustedScore > -1) {
      adjustedResult.riskLevel = 'Высокий';
      adjustedResult.interpretation = 'Высокий риск банкротства.';
    } else {
      adjustedResult.riskLevel = 'Критический';
      adjustedResult.interpretation = 'Критическое положение.';
    }
  } else if (model === 'springate') {
    if (adjustedScore > 0.862) {
      adjustedResult.riskLevel = 'Низкий';
      adjustedResult.interpretation = 'Компания устойчива.';
    } else if (adjustedScore > 0.7) {
      adjustedResult.riskLevel = 'Умеренный';
      adjustedResult.interpretation = 'Умеренный риск.';
    } else if (adjustedScore > 0.5) {
      adjustedResult.riskLevel = 'Высокий';
      adjustedResult.interpretation = 'Высокий риск банкротства.';
    } else {
      adjustedResult.riskLevel = 'Критический';
      adjustedResult.interpretation = 'Критическое положение.';
    }
  } else if (model === 'taffler') {
    if (adjustedScore > 0.3) {
      adjustedResult.riskLevel = 'Низкий';
      adjustedResult.interpretation = 'Хорошие перспективы.';
    } else if (adjustedScore > 0.2) {
      adjustedResult.riskLevel = 'Умеренный';
      adjustedResult.interpretation = 'Неопределенное положение.';
    } else if (adjustedScore > 0.1) {
      adjustedResult.riskLevel = 'Высокий';
      adjustedResult.interpretation = 'Высокий риск банкротства.';
    } else {
      adjustedResult.riskLevel = 'Критический';
      adjustedResult.interpretation = 'Критическое положение.';
    }
  } else if (model === 'beaver') {
    if (adjustedScore > 0.7) {
      adjustedResult.riskLevel = 'Низкий';
      adjustedResult.interpretation = 'Благополучное состояние.';
    } else if (adjustedScore > 0.5) {
      adjustedResult.riskLevel = 'Умеренный';
      adjustedResult.interpretation = 'Признаки ухудшения.';
    } else if (adjustedScore > 0.3) {
      adjustedResult.riskLevel = 'Высокий';
      adjustedResult.interpretation = 'Высокий риск банкротства.';
    } else {
      adjustedResult.riskLevel = 'Критический';
      adjustedResult.interpretation = 'Критическое положение.';
    }
  } else if (model === 'saifullin-kadykov') {
    if (adjustedScore > 1) {
      adjustedResult.riskLevel = 'Низкий';
      adjustedResult.interpretation = 'Хорошее состояние.';
    } else if (adjustedScore > 0.5) {
      adjustedResult.riskLevel = 'Умеренный';
      adjustedResult.interpretation = 'Удовлетворительное состояние.';
    } else if (adjustedScore > 0.2) {
      adjustedResult.riskLevel = 'Высокий';
      adjustedResult.interpretation = 'Неудовлетворительное состояние.';
    } else {
      adjustedResult.riskLevel = 'Критический';
      adjustedResult.interpretation = 'Критическое состояние.';
    }
  }

  adjustedResult.description = `${result.description} (с учетом качественных факторов)`;
  adjustedResult.recommendations = adjustedResult.recommendations || [];
  if (qualitativeFactors.creditHistory === 'poor' || qualitativeFactors.creditHistory === 'very_poor') {
    adjustedResult.recommendations.push('Улучшите кредитную историю.');
  }
  if (qualitativeFactors.pendingLawsuits) {
    adjustedResult.recommendations.push('Разрешите судебные споры.');
  }
  if (qualitativeFactors.delayedPayments) {
    adjustedResult.recommendations.push('Устраните задержки платежей.');
  }
  if (qualitativeFactors.supplierRelationships === 'problematic' || qualitativeFactors.supplierRelationships === 'critical') {
    adjustedResult.recommendations.push('Улучшите отношения с поставщиками.');
  }

  return adjustedResult;
};

/**
 * Adjust analysis data with qualitative factors
 * @param {Object} analysisData - Analysis data object
 * @param {Object} qualitativeFactors - Qualitative factors object
 * @returns {Object} Adjusted analysis data
 */
export const adjustAnalysisWithQualitativeFactors = (analysisData, qualitativeFactors) => {
  const adjustedAnalysisData = { ...analysisData };
  adjustedAnalysisData.qualitativeFactors = {};

  if (qualitativeFactors.creditHistory) {
    adjustedAnalysisData.qualitativeFactors.creditHistory =
      qualitativeFactors.creditHistory === 'excellent' ? 1.0 :
      qualitativeFactors.creditHistory === 'good' ? 0.75 :
      qualitativeFactors.creditHistory === 'average' ? 0.5 :
      qualitativeFactors.creditHistory === 'poor' ? 0.25 : 0;
  }

  adjustedAnalysisData.qualitativeFactors.pendingLawsuits = qualitativeFactors.pendingLawsuits ? 0 : 1;
  adjustedAnalysisData.qualitativeFactors.managementChanges = qualitativeFactors.managementChanges ? 0.25 : 1;
  adjustedAnalysisData.qualitativeFactors.delayedPayments = qualitativeFactors.delayedPayments ? 0 : 1;

  if (qualitativeFactors.supplierRelationships) {
    adjustedAnalysisData.qualitativeFactors.supplierRelationships =
      qualitativeFactors.supplierRelationships === 'excellent' ? 1.0 :
      qualitativeFactors.supplierRelationships === 'good' ? 0.75 :
      qualitativeFactors.supplierRelationships === 'average' ? 0.5 :
      qualitativeFactors.supplierRelationships === 'problematic' ? 0.25 : 0;
  }

  adjustedAnalysisData.weakPoints = adjustedAnalysisData.weakPoints || [];
  if (qualitativeFactors.creditHistory === 'poor' || qualitativeFactors.creditHistory === 'very_poor') {
    adjustedAnalysisData.weakPoints.push('Плохая кредитная история');
  }
  if (qualitativeFactors.pendingLawsuits) {
    adjustedAnalysisData.weakPoints.push('Наличие судебных исков');
  }
  if (qualitativeFactors.delayedPayments) {
    adjustedAnalysisData.weakPoints.push('Систематические задержки платежей');
  }
  if (qualitativeFactors.managementChanges) {
    adjustedAnalysisData.weakPoints.push('Недавние изменения в руководстве');
  }
  if (qualitativeFactors.supplierRelationships === 'problematic' || qualitativeFactors.supplierRelationships === 'critical') {
    adjustedAnalysisData.weakPoints.push('Проблемные отношения с поставщиками');
  }

  if (qualitativeFactors.delayedPayments || qualitativeFactors.creditHistory === 'poor' || qualitativeFactors.creditHistory === 'very_poor') {
    adjustedAnalysisData.trend.overall = 'worsening';
  }

  return adjustedAnalysisData;
};

/**
 * Calculate comprehensive assessment from multiple models
 * @param {Object} results - Results from individual models
 * @param {Object} qualitativeFactors - Qualitative factors object
 * @returns {Object} Comprehensive assessment
 */
export const calculateComprehensiveAssessment = (results, qualitativeFactors = null) => {
  const normalizedScores = {
    altman: results.altman ? normalizeAltmanScore(results.altman.score) : null,
    fulmer: results.fulmer ? normalizeFulmerScore(results.fulmer.score) : null,
    springate: results.springate ? normalizeSpringateScore(results.springate.score) : null,
    taffler: results.taffler ? normalizeTafflerScore(results.taffler.score) : null,
    beaver: results.beaver ? normalizeScore(results.beaver.score, 0, 1) : null,
    saifullin: results.saifullin ? normalizeScore(results.saifullin.score, 0, 2) : null
  };

  const weights = {
    altman: 0.3,
    fulmer: 0.2,
    springate: 0.3,
    taffler: 0.2,
    beaver: 0.15,
    saifullin: 0.15
  };

  let weightedScore = 0, totalWeight = 0;
  for (const model in normalizedScores) {
    if (normalizedScores[model] !== null) {
      weightedScore += normalizedScores[model] * weights[model];
      totalWeight += weights[model];
    }
  }

  if (totalWeight > 0) {
    weightedScore = weightedScore / totalWeight;
  }

  if (qualitativeFactors) {
    let qualitativeScore = 1.0;
    if (qualitativeFactors.creditHistory === 'poor') qualitativeScore -= 0.15;
    else if (qualitativeFactors.creditHistory === 'very_poor') qualitativeScore -= 0.25;
    if (qualitativeFactors.pendingLawsuits) qualitativeScore -= 0.15;
    if (qualitativeFactors.managementChanges) qualitativeScore -= 0.1;
    if (qualitativeFactors.delayedPayments) qualitativeScore -= 0.2;
    if (qualitativeFactors.supplierRelationships === 'problematic') qualitativeScore -= 0.1;
    else if (qualitativeFactors.supplierRelationships === 'critical') qualitativeScore -= 0.15;

    qualitativeScore = Math.max(0, Math.min(1, qualitativeScore));
    weightedScore = weightedScore * 0.7 + qualitativeScore * 0.3;
  }

  let riskLevel, interpretation;
  if (weightedScore > 0.8) {
    riskLevel = 'Очень низкий';
    interpretation = 'Очень низкая вероятность банкротства.';
  } else if (weightedScore > 0.6) {
    riskLevel = 'Низкий';
    interpretation = 'Стабильное финансовое положение.';
  } else if (weightedScore > 0.4) {
    riskLevel = 'Умеренный';
    interpretation = 'Умеренный риск банкротства.';
  } else if (weightedScore > 0.2) {
    riskLevel = 'Высокий';
    interpretation = 'Высокий риск банкротства.';
  } else {
    riskLevel = 'Критический';
    interpretation = 'Высокая вероятность банкротства.';
  }

  const allRecommendations = [];
  for (const model in results) {
    if (results[model]?.recommendations) {
      allRecommendations.push(...results[model].recommendations);
    }
  }
  const uniqueRecommendations = [...new Set(allRecommendations)].slice(0, 5);

  return {
    score: weightedScore,
    riskLevel,
    description: 'Комплексная оценка риска банкротства',
    interpretation,
    recommendations: uniqueRecommendations,
    thresholds: { safe: 0.6, gray: 0.4, distress: 0.2 },
    includedModels: Object.keys(normalizedScores).filter(model => normalizedScores[model] !== null)
  };
};